<template>
  <div class="supervisor-checkinout">
    <md-card>
      <md-card-header class="md-card-header-icon md-card-header-primary">
        <div class="card-icon">
          <md-icon>fact_check</md-icon>
        </div>
        <h4 class="title">Supervisor Check-In / Check-Out</h4>
        <p class="category">Manage employee attendance for your work points</p>
      </md-card-header>

      <md-card-content>
        <!-- Department Info -->
        <div class="department-info" v-if="userDepartment">
          <md-chip class="md-accent">
            <md-icon>business</md-icon>
            <span>Department: {{ userDepartment.name }}</span>
          </md-chip>
          <md-chip v-if="hasCrossDepartmentAccess" class="md-primary">
            <md-icon>public</md-icon>
            <span>Cross Department Access</span>
          </md-chip>
        </div>

        <!-- Filters -->
        <div class="md-layout md-gutter mt-3">
          <!-- Department Filter (only for cross-department users) -->
          <div class="md-layout-item md-size-25" v-if="hasCrossDepartmentAccess">
            <select-input
                v-model="filters.department"
                track-by="id"
                label="Department Filter"
                placeholder="All Departments"
                :options="departments"
                :allow-empty="true"
                class="filter-dropdown"
                @input="onFilterChange"
            />
          </div>

          <div class="md-layout-item" :class="hasCrossDepartmentAccess ? 'md-size-25' : 'md-size-33'">
            <select-input
                v-model="filters.workplace"
                track-by="id"
                label="Work Point Filter"
                placeholder="All Work Points"
                :options="workPoints"
                :allow-empty="true"
                class="filter-dropdown"
                @input="onFilterChange"
            />
          </div>

          <div class="md-layout-item" :class="hasCrossDepartmentAccess ? 'md-size-25' : 'md-size-33'">
            <md-field>
              <label>Select Date</label>
              <md-input type="date" v-model="filters.date" @change="onFilterChange"></md-input>
            </md-field>
          </div>

          <div class="md-layout-item" :class="hasCrossDepartmentAccess ? 'md-size-25' : 'md-size-33'">
            <select-input
                v-model="filters.status"
                track-by="id"
                label="Status Filter"
                placeholder="All Status"
                :options="statusOptions"
                :allow-empty="true"
                class="filter-dropdown"
                @input="onFilterChange"
            />
          </div>
        </div>

        <!-- Stats Cards -->
        <div class="stats-cards md-layout md-gutter">
          <div class="md-layout-item md-size-25" @click="setStatusFilter('Check In')">
            <div class="stat-card checkin" :class="{ active: filters.status === 'Check In' }">
              <div class="stat-value">{{ stats['Check In'] || 0 }}</div>
              <div class="stat-label">Check In</div>
            </div>
          </div>
          <div class="md-layout-item md-size-25" @click="setStatusFilter('Checkin Supervisor')">
            <div class="stat-card checkin_supervisor" :class="{ active: filters.status === 'Checkin Supervisor' }">
              <div class="stat-value">{{ stats['Checkin Supervisor'] || 0 }}</div>
              <div class="stat-label">Checkin Supervisor</div>
            </div>
          </div>
          <div class="md-layout-item md-size-25" @click="setStatusFilter('Checkout Supervisor')">
            <div class="stat-card checkout_supervisor" :class="{ active: filters.status === 'Checkout Supervisor' }">
              <div class="stat-value">{{ stats['Checkout Supervisor'] || 0 }}</div>
              <div class="stat-label">Checkout Supervisor</div>
            </div>
          </div>
          <div class="md-layout-item md-size-25" @click="setStatusFilter('Check Out')">
            <div class="stat-card checkout" :class="{ active: filters.status === 'Check Out' }">
              <div class="stat-value">{{ stats['Check Out'] || 0 }}</div>
              <div class="stat-label">Check Out</div>
            </div>
          </div>
        </div>

        <!-- Auto-refresh Indicator -->
        <div class="auto-refresh-bar">
          <div class="auto-refresh-controls">

            <span class="auto-refresh-text">
               <md-icon class="auto-refresh-icon" :class="{ 'spinning': isRefreshing }">autorenew</md-icon>
              Auto-refresh every
      <span class="interval-value"  @click="increaseRefreshInterval">{{ refreshInterval / 1000 }}</span> seconds
               <md-button
                   class="md-icon-button"
                   @click="manualRefresh"
                   title="Refresh Now"
               >
              <md-icon>refresh</md-icon>
            </md-button>
    </span>


          </div>
        </div>

        <!-- Loading -->
        <div v-if="isPageLoading" class="loading-container">
          <md-progress-spinner md-mode="indeterminate"></md-progress-spinner>
        </div>

        <!-- Labourers Table -->
        <div class="labourers-table" v-else>
          <md-table v-if="labourerSchedules && labourerSchedules.length">

            <md-table-toolbar>

              <md-field>
                <md-input
                    v-model="filters.query"
                    type="search"
                    class="mb-3"
                    clearable
                    style="width: 200px"
                    placeholder="Search ..."
                >
                </md-input>
              </md-field>
            </md-table-toolbar>
            <md-table-row>
              <md-table-head>Labourer</md-table-head>
              <md-table-head>Work Point</md-table-head>
              <md-table-head>Activity</md-table-head>
              <md-table-head>Shift</md-table-head>
              <md-table-head>Takeon Number</md-table-head>
              <md-table-head>Status</md-table-head>
              <md-table-head>Check In</md-table-head>
              <md-table-head>Checkin Supervisor</md-table-head>
              <md-table-head>Checkout Supervisor</md-table-head>
              <md-table-head>Check Out</md-table-head>
              <md-table-head>Actions</md-table-head>
            </md-table-row>

            <md-table-row v-for="item in labourerSchedules" :key="item.id">
              <md-table-cell>
                <strong>{{ getFullName(item.labourer) }}</strong>
                <br/>
                <small class="text-muted">{{ item.labourer ? item.labourer.phone_number : '' }}</small>
              </md-table-cell>

              <md-table-cell>{{ getWorkPointNameFromItem(item) }}</md-table-cell>
              <md-table-cell>{{ getActivityNameFromItem(item) }}</md-table-cell>

              <md-table-cell>
                {{ getShiftNameFromItem(item) }}
                <br/>
                <small>{{ getShiftTimeFromItem(item) }}</small>
              </md-table-cell>

              <md-table-cell>
                <badge type="info">{{ item.takeon_number }}</badge>
              </md-table-cell>

              <md-table-cell>
                <badge :type="getStatusBadge(getStatusText(item))">
                  {{ getStatusText(item) }}
                </badge>
              </md-table-cell>

              <md-table-cell>
                <div v-if="item.checkin_at">
                  {{ formatTime(item.checkin_at) }}
                  <br/>
                  <badge :type="getCheckTypeBadge(item.check_in_type)" size="small">
                    {{ getCheckTypeLabel(item.check_in_type) }}
                  </badge>
                </div>
                <span v-else class="text-muted">Not checked in</span>
              </md-table-cell>

              <md-table-cell>
                <div v-if="item.supervisor_checkin_at">
                  {{ formatTime(item.supervisor_checkin_at) }}
                  <badge :type="getReactionBadge(item.checkin_supervisor_reaction)" size="small">
                    {{ getReactionLabel(item.checkin_supervisor_reaction) }}
                  </badge>
                  <br/>
                  <small class="text-muted">{{ item.checkin_supervisor_comment }}</small>
                </div>
                <span v-else class="text-muted">Pending</span>
              </md-table-cell>

              <md-table-cell>
                <div v-if="item.supervisor_checkout_at">
                  {{ formatTime(item.supervisor_checkout_at) }}
                  <badge :type="getReactionBadge(item.checkout_supervisor_reaction)" size="small">
                    {{ getReactionLabel(item.checkout_supervisor_reaction) }}
                  </badge>
                  <br/>
                  <small class="text-muted">{{ item.supervisor_checkout_comment }}</small>
                </div>
                <span v-else class="text-muted">Pending</span>
              </md-table-cell>

              <md-table-cell>
                <div v-if="item.checkout_at">
                  {{ formatTime(item.checkout_at) }}
                  <br/>
                  <badge :type="getCheckTypeBadge(item.check_out_type)" size="small">
                    {{ getCheckTypeLabel(item.check_out_type) }}
                  </badge>
                </div>
                <span v-else class="text-muted">Not checked out</span>
              </md-table-cell>

              <md-table-cell>
                <div class="action-buttons">
                  <md-button
                      v-if="canShowCheckInButton(item)"
                      class="md-primary md-raised md-dense"
                      @click="showCheckIn(item)"
                      :disabled="isProcessing"
                  >
                    <md-icon>login</md-icon>
                    Check In
                  </md-button>

                  <md-button
                      v-if="canShowCheckinSupervisorButton(item)"
                      class="md-success md-raised md-dense"
                      @click="showCheckinSupervisor(item)"
                      :disabled="isProcessing"
                  >
                    <md-icon>verified</md-icon>
                    Checkin Supervisor
                  </md-button>

                  <md-button
                      v-if="canShowCheckoutSupervisorButton(item)"
                      class="md-warning md-raised md-dense"
                      @click="showCheckoutSupervisor(item)"
                      :disabled="isProcessing"
                  >
                    <md-icon>exit_to_app</md-icon>
                    Checkout Supervisor
                  </md-button>

                  <md-button
                      v-if="canShowCheckOutButton(item)"
                      class="md-primary md-raised md-dense"
                      @click="showCheckOut(item)"
                      :disabled="isProcessing"
                  >
                    <md-icon>logout</md-icon>
                    Check Out
                  </md-button>
                </div>
              </md-table-cell>
            </md-table-row>
          </md-table>

          <div v-else class="empty-state">
            <md-icon>people_outline</md-icon>
            <p>No labourers found for the selected criteria</p>
          </div>
        </div>

        <!-- Pagination -->
        <div class="pagination-footer">
          <div class="per-page-selector" style="display: flex; align-items: center;">
            <span>Show:</span>
            <select-input
                v-model="pagination.perPage"
                :options="perPageOptions"
                :reduce-to-value="false"
                class="filter-dropdown"
                style="width: 80px; margin: 0 8px;"
                @input="onPerPageChange"
            />
            <span>entries</span>
          </div>
          <div>
            <p class="card-category">
              Showing {{ from + 1 }} to {{ to }} of {{ totalItems }} entries
            </p>
          </div>
          <pagination
              class="pagination-no-border pagination-success"
              v-model="pagination.currentPage"
              :per-page="pagination.perPage"
              :total="totalItems"
              @update:model-value="onPageChange"
          />
        </div>
      </md-card-content>
    </md-card>

    <!-- Modals (same as before) -->
    <!-- Check In Modal -->
    <md-dialog :md-active.sync="modal.checkIn.active">
      <md-dialog-title>Check In</md-dialog-title>
      <md-dialog-content>
        <div class="modal-content">
          <p><strong>Labourer:</strong> {{ getFullName(selectedLabourer) }}</p>
          <p><strong>Work Point:</strong> {{ getWorkPointNameFromItem(selectedSchedule) }}</p>
          <p><strong>Activity:</strong> {{ getActivityNameFromItem(selectedSchedule) }}</p>
          <p><strong>Takeon Number:</strong> {{ selectedSchedule ? selectedSchedule.takeon_number : '' }}</p>

          <md-field>
            <label>Check In Time</label>
            <md-input v-model="modal.checkIn.time" type="datetime-local"/>
          </md-field>

          <md-field>
            <label>Remarks (Optional)</label>
            <md-textarea v-model="modal.checkIn.remarks"/>
          </md-field>
        </div>
      </md-dialog-content>
      <md-dialog-actions>
        <md-button class="md-primary" @click="confirmCheckIn">Confirm Check In</md-button>
        <md-button class="md-danger" @click="modal.checkIn.active = false">Cancel</md-button>
      </md-dialog-actions>
    </md-dialog>

    <!-- Checkin Supervisor Modal -->
    <md-dialog :md-active.sync="modal.checkinSupervisor.active" class="supervisor-dialog">
      <md-dialog-title>Checkin Supervisor</md-dialog-title>
      <md-dialog-content>
        <div class="modal-content">
          <div class="labourer-info">
            <p><strong>Labourer:</strong> {{ getFullName(selectedLabourer) }}</p>
            <p><strong>Work Point:</strong> {{ getWorkPointNameFromItem(selectedSchedule) }}</p>
            <p><strong>Activity:</strong> {{ getActivityNameFromItem(selectedSchedule) }}</p>
            <p><strong>Takeon Number:</strong> {{ selectedSchedule ? selectedSchedule.takeon_number : '' }}</p>
          </div>

          <md-field>
            <label>Status *</label>
            <md-select v-model="modal.checkinSupervisor.reaction" required>
              <md-option value="on_time">On Time</md-option>
              <md-option value="late">Late</md-option>
              <md-option value="absent">Absent</md-option>
            </md-select>
          </md-field>

          <md-field>
            <label>Confirmation Time</label>
            <md-input v-model="modal.checkinSupervisor.time" type="datetime-local"/>
          </md-field>

          <md-field>
            <label>Comments (Optional)</label>
            <md-textarea v-model="modal.checkinSupervisor.comment" rows="3"/>
          </md-field>
        </div>
      </md-dialog-content>
      <md-dialog-actions>
        <md-button class="md-success" @click="confirmCheckinSupervisor">Confirm Checkin Supervisor</md-button>
        <md-button class="md-danger" @click="modal.checkinSupervisor.active = false">Cancel</md-button>
      </md-dialog-actions>
    </md-dialog>

    <!-- Checkout Supervisor Modal -->
    <md-dialog :md-active.sync="modal.checkoutSupervisor.active" class="supervisor-dialog">
      <md-dialog-title>Checkout Supervisor</md-dialog-title>
      <md-dialog-content>
        <div class="modal-content">
          <div class="labourer-info">
            <p><strong>Labourer:</strong> {{ getFullName(selectedLabourer) }}</p>
            <p><strong>Work Point:</strong> {{ getWorkPointNameFromItem(selectedSchedule) }}</p>
            <p><strong>Activity:</strong> {{ getActivityNameFromItem(selectedSchedule) }}</p>
            <p><strong>Takeon Number:</strong> {{ selectedSchedule ? selectedSchedule.takeon_number : '' }}</p>
          </div>

          <md-field>
            <label>Completion Status *</label>
            <md-select v-model="modal.checkoutSupervisor.reaction" required>
              <md-option value="completed">Completed</md-option>
              <md-option value="partial">Partial Completion</md-option>
              <md-option value="early_departure">Early Departure</md-option>
            </md-select>
          </md-field>

          <md-field>
            <label>Release Time</label>
            <md-input v-model="modal.checkoutSupervisor.time" type="datetime-local"/>
          </md-field>

          <md-field>
            <label>Comments (Optional)</label>
            <md-textarea v-model="modal.checkoutSupervisor.comment" rows="3"/>
          </md-field>
        </div>
      </md-dialog-content>
      <md-dialog-actions>
        <md-button class="md-warning" @click="confirmCheckoutSupervisor">Confirm Checkout Supervisor</md-button>
        <md-button class="md-danger" @click="modal.checkoutSupervisor.active = false">Cancel</md-button>
      </md-dialog-actions>
    </md-dialog>

    <!-- Check Out Modal -->
    <md-dialog :md-active.sync="modal.checkOut.active">
      <md-dialog-title>Check Out</md-dialog-title>
      <md-dialog-content>
        <div class="modal-content">
          <p><strong>Labourer:</strong> {{ getFullName(selectedLabourer) }}</p>
          <p><strong>Work Point:</strong> {{ getWorkPointNameFromItem(selectedSchedule) }}</p>
          <p><strong>Activity:</strong> {{ getActivityNameFromItem(selectedSchedule) }}</p>
          <p><strong>Takeon Number:</strong> {{ selectedSchedule ? selectedSchedule.takeon_number : '' }}</p>

          <md-field>
            <label>Check Out Time</label>
            <md-input v-model="modal.checkOut.time" type="datetime-local"/>
          </md-field>

          <md-field>
            <label>Remarks (Optional)</label>
            <md-textarea v-model="modal.checkOut.remarks"/>
          </md-field>
        </div>
      </md-dialog-content>
      <md-dialog-actions>
        <md-button class="md-primary" @click="confirmCheckOut">Confirm Check Out</md-button>
        <md-button class="md-danger" @click="modal.checkOut.active = false">Cancel</md-button>
      </md-dialog-actions>
    </md-dialog>
  </div>
</template>

<script>
import {mapActions, mapGetters} from 'vuex';
import Badge from '@/components/Badge.vue';
import Pagination from '@/components/Pagination';
import SelectInput from "@/components/Inputs/SelectInput.vue";

export default {
  name: 'SupervisorCheckInOut',
  components: {
    SelectInput,
    Badge,
    Pagination
  },
  data: function () {
    return {
      labourerSchedulesData: [],
      filters: {
        department: '',
        workplace: '',
        date: null,
        query: null,
        status: ''
      },
      workPoints: [],
      departments: [],
      selectedSchedule: null,
      userId: null,
      selectedLabourer: null,
      isProcessing: false,
      isPageLoading: false,
      pagination: {
        currentPage: 1,
        perPage: 10
      },
      perPageOptions: [10, 25, 50, 100],
      totalItems: 0,
      stats: {
        'Check In': 0,
        'Checkin Supervisor': 0,
        'Checkout Supervisor': 0,
        'Check Out': 0
      },
      modal: {
        checkIn: {active: false, time: null, remarks: ''},
        checkinSupervisor: {active: false, time: null, reaction: 'on_time', comment: ''},
        checkoutSupervisor: {active: false, time: null, reaction: 'completed', comment: ''},
        checkOut: {active: false, time: null, remarks: ''}
      },
      pollingInterval: null,
      lastRefreshTime: null,
      refreshInterval: 60000,
      refreshIntervalOptions: [30000, 60000, 90000, 120000],
      refreshIntervalIndex: 1,
      isRefreshing: false,
      statusOptions: [
        {'id': 'Check In', 'name': 'Check In'},
        {'id': 'Checkin Supervisor', 'name': 'Checkin Supervisor'},
        {'id': 'Checkout Supervisor', 'name': 'Checkout Supervisor'},
        {'id': 'Check Out', 'name': 'Check Out'},
      ]
    };
  },
  computed: {
    ...mapGetters('labourerSchedules', ['isLoading']),
    ...mapGetters('profile', ['me']),
    ...mapGetters('departments', {departmentsList: 'list'}),

    labourerSchedules: function () {
      return this.labourerSchedulesData;
    },

    userDepartment: function () {
      if (this.me && this.me.model && this.me.model.department) {
        return this.me.model.department;
      }
      return null;
    },

    hasCrossDepartmentAccess: function () {
      if (this.me && this.me.roles) {
        return this.me.roles.some(function (role) {
          return role.name === 'cross department' ||
              role.name === 'cross port' ||
              role.name === 'cross station';
        });
      }
      return false;
    },

    currentDateTime: function () {
      var now = new Date();
      now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
      return now.toISOString().slice(0, 16);
    },

    from: function () {
      return this.pagination.perPage * (this.pagination.currentPage - 1);
    },
    to: function () {
      var highBound = this.from + this.pagination.perPage;
      if (this.totalItems < highBound) {
        highBound = this.totalItems;
      }
      return highBound;
    }
  },
  watch: {
    'filters.query': function (newVal, oldVal) {
      clearTimeout(this.debounceTimeout);
      this.debounceTimeout = setTimeout(() => {
        this.fetchData();
      }, 500);
    },
    userDepartment: {
      immediate: true,
      handler(newDept) {
        if (newDept) {
          console.log('[watch:userDepartment] Department detected:', newDept);
          this.filters.department = newDept.id;
          this.user_department = newDept;
          this.fetchData();
        } else {
          console.log('[watch:userDepartment] No department found.');
        }
      }
    }
  },
  created: function () {
    this.userId = this.me ? this.me.id : null;
    this.filters.date = this.formatDateYMD(new Date());
    this.fetchDepartments();
    this.fetchWorkPoints();
    this.fetchData();
    this.startPolling();
  },
  beforeDestroy: function () {
    this.stopPolling();
  },
  methods: {
    ...mapActions('labourerSchedules', [
      'list',
      'checkin',
      'checkout',
      'checkinSupervisor',
      'checkoutSupervisor'
    ]),
    ...mapActions('departments', {
      fetchDepartmentsAction: 'list'
    }),

    async fetchDepartments() {
      try {
        await this.fetchDepartmentsAction({page: {size: 1000}});
        this.departments = this.departmentsList || [];
      } catch (error) {
        console.error('Error fetching departments:', error);
      }
    },

    async fetchData(silent = false) {
      if (!silent) {
        this.isPageLoading = true;
      }

      try {
        var params = {
          include: 'labourer,schedule.activityShift.activityGuidance.guidanceWorkplace.workplace,' +
              'schedule.activityShift.activityGuidance.activity,schedule.activityShift.shift',
          filter: {},
          page: {
            number: this.pagination.currentPage,
            size: this.pagination.perPage
          }
        };


        if (this.hasCrossDepartmentAccess) {

          if (this.filters.department) {
            params.filter.department = this.filters.department;
          }

          else if (this.userDepartment && this.userDepartment.id) {
            params.filter.department = this.userDepartment.id;
          }
        }

        else if (this.userDepartment && this.userDepartment.id) {
          params.filter.department = this.userDepartment.id;
        }


        if (this.filters.workplace) {
          params.filter.workplace_id = this.filters.workplace;
        }


        if (this.filters.date) {
          params.filter.shift_date = this.filters.date;
        }


        if (this.filters.status) {
          params.filter.status = this.filters.status;
        }


        if (this.filters.query) {
          params.filter.search = this.filters.query;
        }

        await this.list(params);
        var response = this.$store.getters['labourerSchedules/list'];
        this.totalItems = this.$store.getters['labourerSchedules/listTotal'] || 0;

        if (response) {
          this.labourerSchedulesData = response;
        } else {
          this.labourerSchedulesData = [];
        }

        if (response && response.meta && response.meta.stats) {
          this.stats = response.meta.stats;
        }

        this.lastRefreshTime = new Date();

      } catch (error) {
        console.error('Error fetching data:', error);
        if (!silent) {
          this.notifyError('Failed to load attendance data');
        }
        this.labourerSchedulesData = [];
        this.totalItems = 0;
      } finally {
        if (!silent) {
          this.isPageLoading = false;
        }
      }
    },

    async fetchWorkPoints() {
      try {

        await this.$store.dispatch('workplaces/list');
        this.workPoints = this.$store.getters['workplaces/list'] || [];
      } catch (error) {
        this.notifyError('Failed to load workplace data');
        console.error('Error fetching work points:', error);
      }
    },

    onFilterChange: function () {
      this.pagination.currentPage = 1;
      this.fetchData();
    },

    onPageChange: function (page) {
      this.pagination.currentPage = page;
      this.fetchData();
    },

    onPerPageChange: function () {
      this.pagination.currentPage = 1;
      this.fetchData();
    },

    setStatusFilter: function (status) {
      this.filters.status = this.filters.status === status ? '' : status;
      this.pagination.currentPage = 1;
      this.fetchData();
    },

    getStatusText: function (schedule) {
      let statusText = schedule.status;
      if (typeof statusText === 'object') {
        statusText = statusText.name;
      }
      return statusText;
    },

    canShowCheckInButton: function (schedule) {
      let statusText = this.getStatusText(schedule);
      return (statusText === 'Assigned' || statusText === 'pending' || statusText === 'Created') && !schedule.checkin_at;
    },

    canShowCheckinSupervisorButton: function (schedule) {
      let statusText = this.getStatusText(schedule);
      return statusText === 'Check In' && !schedule.supervisor_checkin_at;
    },

    canShowCheckoutSupervisorButton: function (schedule) {
      let statusText = this.getStatusText(schedule);
      return statusText === 'Checkin Supervisor' && !schedule.supervisor_checkout_at;
    },

    canShowCheckOutButton: function (schedule) {
      let statusText = this.getStatusText(schedule);
      return statusText === 'Checkout Supervisor' && !schedule.checkout_at;
    },

    showCheckIn: function (schedule) {
      this.selectedSchedule = schedule;
      this.selectedLabourer = schedule.labourer;
      this.modal.checkIn.time = this.currentDateTime;
      this.modal.checkIn.remarks = '';
      this.modal.checkIn.active = true;
    },

    async confirmCheckIn() {
      if (!this.selectedSchedule) return;
      this.isProcessing = true;

      try {
        this.selectedSchedule.checkin_at = this.modal.checkIn.time;
        this.selectedSchedule.check_in_type = 'manual';
        this.selectedSchedule.assignment_remark = this.modal.checkIn.remarks;
        await this.checkin(this.selectedSchedule);
        this.notifySuccess('Check in completed');
        this.modal.checkIn.active = false;
        await this.fetchData();
      } catch (error) {
        this.notifyException(error);
      } finally {
        this.isProcessing = false;
      }
    },

    showCheckinSupervisor: function (schedule) {
      this.selectedSchedule = schedule;
      this.selectedLabourer = schedule.labourer;
      this.modal.checkinSupervisor.time = this.currentDateTime;
      this.modal.checkinSupervisor.reaction = 'on_time';
      this.modal.checkinSupervisor.comment = '';
      this.modal.checkinSupervisor.active = true;
    },

    async confirmCheckinSupervisor() {
      if (!this.selectedSchedule) return;
      this.isProcessing = true;

      try {
        const payload = {
          id: this.selectedSchedule.id,
          checkin_supervisor_reaction: this.modal.checkinSupervisor.reaction,
          checkin_supervisor_comment: this.modal.checkinSupervisor.comment,
          supervisor_checkin_at: this.modal.checkinSupervisor.time,
          checkin_supervisor_id: this.userId
        };
        await this.checkinSupervisor(payload);
        this.notifySuccess('Checkin Supervisor confirmed');
        this.modal.checkinSupervisor.active = false;
        await this.fetchData();
      } catch (error) {
        console.error('Error:', error);
        this.$store.dispatch('alerts/error', 'Failed to confirm');
      } finally {
        this.isProcessing = false;
      }
    },

    showCheckoutSupervisor: function (schedule) {
      this.selectedSchedule = schedule;
      this.selectedLabourer = schedule.labourer;
      this.modal.checkoutSupervisor.time = this.currentDateTime;
      this.modal.checkoutSupervisor.reaction = 'completed';
      this.modal.checkoutSupervisor.comment = '';
      this.modal.checkoutSupervisor.active = true;
    },

    async confirmCheckoutSupervisor() {
      if (!this.selectedSchedule) return;
      this.isProcessing = true;

      try {
        const payload = {
          id: this.selectedSchedule.id,
          checkout_supervisor_reaction: this.modal.checkoutSupervisor.reaction,
          supervisor_checkout_comment: this.modal.checkoutSupervisor.comment,
          supervisor_checkout_at: this.modal.checkoutSupervisor.time,
          checkout_supervisor_id: this.userId
        };
        await this.checkoutSupervisor(payload);
        this.notifySuccess('Checkout Supervisor confirmed');
        this.modal.checkoutSupervisor.active = false;
        await this.fetchData();
      } catch (error) {
        console.error('Error:', error);
        this.$store.dispatch('alerts/error', 'Failed to confirm');
      } finally {
        this.isProcessing = false;
      }
    },

    showCheckOut: function (schedule) {
      this.selectedSchedule = schedule;
      this.selectedLabourer = schedule.labourer;
      this.modal.checkOut.time = this.currentDateTime;
      this.modal.checkOut.remarks = '';
      this.modal.checkOut.active = true;
    },

    async confirmCheckOut() {
      if (!this.selectedSchedule) return;
      this.isProcessing = true;

      try {
        await this.checkout({
          id: this.selectedSchedule.id,
          checkout_at: this.modal.checkOut.time,
          check_out_type: 'manual',
          checkout_comment: this.modal.checkOut.remarks
        });
        this.$store.dispatch('alerts/success', 'Check out completed');
        this.modal.checkOut.active = false;
        await this.fetchData();
      } catch (error) {
        console.error('Error:', error);
        this.notifyException(error);
      } finally {
        this.isProcessing = false;
      }
    },

    getWorkPointNameFromItem: function (item) {
      try {
        if (item.schedule && item.schedule.activityShift &&
            item.schedule.activityShift.activityGuidance &&
            item.schedule.activityShift.activityGuidance.guidanceWorkplace) {
          var workplace = item.schedule.activityShift.activityGuidance.guidanceWorkplace.workplace;
          return workplace ? workplace.name : 'N/A';
        }
      } catch (e) {
        return 'N/A';
      }
      return 'N/A';
    },

    getActivityNameFromItem: function (item) {
      try {
        if (item.schedule && item.schedule.activityShift &&
            item.schedule.activityShift.activityGuidance &&
            item.schedule.activityShift.activityGuidance.activity) {
          return item.schedule.activityShift.activityGuidance.activity.name || 'N/A';
        }
      } catch (e) {
        return 'N/A';
      }
      return 'N/A';
    },

    getShiftNameFromItem: function (item) {
      try {
        if (item.schedule && item.schedule.activityShift && item.schedule.activityShift.shift) {
          return item.schedule.activityShift.shift.name || 'N/A';
        }
      } catch (e) {
        return 'N/A';
      }
      return 'N/A';
    },

    getShiftTimeFromItem: function (item) {
      try {
        if (item.schedule && item.schedule.activityShift && item.schedule.activityShift.shift) {
          var shift = item.schedule.activityShift.shift;
          return (shift.start_time || '') + ' - ' + (shift.end_time || '');
        }
      } catch (e) {
        return '';
      }
      return '';
    },

    getFullName: function (labourer) {
      if (!labourer) return 'N/A';
      var parts = [];
      if (labourer.first_name) parts.push(labourer.first_name);
      if (labourer.middle_name) parts.push(labourer.middle_name);
      if (labourer.last_name) parts.push(labourer.last_name);
      return parts.length ? parts.join(' ') : 'N/A';
    },

    getStatusBadge: function (status) {
      const badges = {
        'Check In': 'primary',
        'Checkin Supervisor': 'success',
        'Checkout Supervisor': 'warning',
        'Check Out': 'success'
      };
      return badges[status] || 'info';
    },

    getReactionLabel: function (reaction) {
      var labels = {
        'on_time': 'On Time',
        'late': 'Late',
        'completed': 'Completed',
        'partial': 'Partial',
        'early_departure': 'Early Departure'
      };
      return labels[reaction] || reaction;
    },

    getReactionBadge: function (reaction) {
      var badges = {
        'on_time': 'success',
        'late': 'warning',
        'completed': 'success',
        'partial': 'warning',
        'early_departure': 'warning'
      };
      return badges[reaction] || 'info';
    },

    getCheckTypeLabel: function (type) {
      var labels = {'terminal': 'Terminal', 'manual': 'Manual', 'auto': 'Auto'};
      return labels[type] || type;
    },

    getCheckTypeBadge: function (type) {
      var badges = {'terminal': 'info', 'manual': 'warning', 'auto': 'success'};
      return badges[type] || 'primary';
    },

    formatDateYMD: function (date) {
      if (!date) return null;
      var d = new Date(date);
      var year = d.getFullYear();
      var month = String(d.getMonth() + 1).padStart(2, '0');
      var day = String(d.getDate()).padStart(2, '0');
      return year + '-' + month + '-' + day;
    },

    startPolling: function () {
      var self = this;
      if (this.pollingInterval) {
        clearInterval(this.pollingInterval);
      }
      this.pollingInterval = setInterval(function () {
        self.fetchData(true); // Silent refresh
      }, this.refreshInterval);
    },

    stopPolling: function () {
      if (this.pollingInterval) {
        clearInterval(this.pollingInterval);
        this.pollingInterval = null;
      }
    },

    increaseRefreshInterval: function () {
      if (this.refreshIntervalIndex < this.refreshIntervalOptions.length - 1) {
        this.refreshIntervalIndex++;

      }else{
        this.refreshIntervalIndex=0;
      }
      this.refreshInterval = this.refreshIntervalOptions[this.refreshIntervalIndex];
      this.restartPolling();
      this.notifyInfo('Auto-refresh interval set to ' + (this.refreshInterval / 1000) + ' seconds');
    },

    decreaseRefreshInterval: function () {
      if (this.refreshIntervalIndex > 0) {
        this.refreshIntervalIndex--;
        this.refreshInterval = this.refreshIntervalOptions[this.refreshIntervalIndex];
        this.restartPolling();
        this.notifyInfo('Auto-refresh interval set to ' + (this.refreshInterval / 1000) + ' seconds');
      }
    },

    restartPolling: function () {
      this.stopPolling();
      this.startPolling();
    },

    manualRefresh: function () {
      var self = this;
      this.isRefreshing = true;
      this.fetchData(false).finally(function () {
        setTimeout(function () {
          self.isRefreshing = false;
        }, 500);
      });
      this.notifyInfo('Refreshing data...');
    },

    formatTime: function (date) {
      if (!date) return '';
      var d = new Date(date);
      return d.toLocaleTimeString();
    }
  }
};
</script>

<style scoped>
.supervisor-checkinout {
  padding: 20px;
}

.department-info {
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
  flex-wrap: wrap;
}

.stats-cards {
  margin-bottom: 24px;
}

.stat-card {
  background: white;
  border-radius: 8px;
  padding: 15px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.stat-card.active {
  border: 2px solid #2196f3;
  transform: scale(1.02);
}

.stat-card.checkin {
  border-left: 4px solid #2196f3;
}

.stat-card.checkin_supervisor {
  border-left: 4px solid #4caf50;
}

.stat-card.checkout_supervisor {
  border-left: 4px solid #ff9800;
}

.stat-card.checkout {
  border-left: 4px solid #4caf50;
}

.stat-value {
  font-size: 28px;
  font-weight: bold;
  margin-bottom: 5px;
}

.stat-label {
  font-size: 12px;
  color: #666;
}

.auto-refresh-bar {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
  padding: 8px 12px;
  background: #f5f5f5;
  border-radius: 8px;
  font-size: 12px;
}

.auto-refresh-text {
  display: flex;
  align-items: center;
  gap: 4px;
  color: #666;
}

.auto-refresh-text md-icon {
  font-size: 16px;
}

.action-buttons {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: #999;
}

.empty-state md-icon {
  font-size: 48px;
  margin-bottom: 16px;
}

.pagination-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 20px;
  padding-top: 15px;
  border-top: 1px solid #e0e0e0;
  flex-wrap: wrap;
  gap: 16px;
}

.per-page-selector {
  display: flex;
  align-items: center;
  gap: 8px;
}

.per-page-select {
  width: 80px;
  margin: 0;
}

.modal-content {
  min-width: 400px;
}

.text-muted {
  color: #6c757d;
  font-size: 12px;
}

.mt-3 {
  margin-top: 16px;
}

.loading-container {
  display: flex;
  justify-content: center;
  padding: 40px;
}

@media (max-width: 768px) {
  .supervisor-checkinout {
    padding: 10px;
  }

  .modal-content {
    min-width: auto;
  }

  .action-buttons {
    flex-direction: column;
  }

  .pagination-footer {
    flex-direction: column;
    align-items: center;
  }

  /* Add to existing styles */
  .auto-refresh-bar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
    padding: 8px 16px;
    background: linear-gradient(135deg, #f5f5f5 0%, #e8e8e8 100%);
    border-radius: 8px;
    font-size: 13px;
    flex-wrap: wrap;
    gap: 10px;
  }

  .auto-refresh-controls {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }

  .auto-refresh-icon {
    color: #9c27b0;
  }

  .auto-refresh-icon.spinning {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }

  .auto-refresh-text {
    display: flex;
    align-items: center;
    gap: 4px;
    color: #555;
  }

  .interval-value {
    font-weight: bold;
    min-width: 40px;
    text-align: center;
    color: #9c27b0;
  }

  .last-refresh-time {
    display: flex;
    align-items: center;
    gap: 4px;
    color: #888;
    font-size: 12px;
  }

  .last-refresh-time md-icon {
    font-size: 16px;
  }

  .md-button.md-small {
    min-width: 32px;
    height: 32px;
    width: 32px;
    margin: 0;
    padding: 0;
  }

  .md-button.md-small .md-icon {
    font-size: 18px;
    line-height: 18px;
  }
}
</style>